/**
 * sourceTruth — one evidence-based source-truth vocabulary for the operator surfaces
 * (FULL-0161 / PROD-UX-075).
 *
 * The labels LIVE / POLLING / CACHED / STALE / UNAVAILABLE (plus loading, denied and unknown) are
 * derived only from authoritative evidence:
 *   - react-query telemetry: dataUpdatedAt (a successful read), isError, fetchStatus, the HTTP
 *     status on the error, and the caller's enabled flag;
 *   - the browser's own online/offline signal (navigator.onLine + events);
 *   - the polling interval the caller actually configured;
 *   - an explicit push signal (connected + lastEventAt) for LIVE.
 *
 * No label is inferred from a timer, from the fact that a query exists, or from the caller's hope.
 * In particular:
 *   - POLLING requires a successful read while online with a real poller;
 *   - CACHED is a held successful read that is not currently refreshing (offline/paused/no poller);
 *   - STALE is a held successful read whose latest refresh failed;
 *   - LIVE requires an explicit, connected push source; polling alone never claims LIVE;
 *   - UNAVAILABLE means there is no usable payload, never "the request has not finished yet";
 *   - DENIED (401/403) is never masked as CACHED or POLLING.
 *
 * Pure module: no JSX, no data access, no clock reads.
 */

export const SOURCE_TRUTH = {
  LIVE: "LIVE",
  POLLING: "POLLING",
  CACHED: "CACHED",
  STALE: "STALE",
  UNAVAILABLE: "UNAVAILABLE",
  DENIED: "DENIED",
  UNKNOWN: "UNKNOWN",
  LOADING: "LOADING",
};

export const SOURCE_TRUTH_META = {
  [SOURCE_TRUTH.LIVE]: {
    label: "חי",
    hint: "מקור דחיפה מחובר ומדווח אירועים בזמן אמת.",
    cls: "bg-green-100 text-green-800 border-green-200",
  },
  [SOURCE_TRUTH.POLLING]: {
    label: "נסקר",
    hint: "המקור נבדק מחדש בשרת לפי מרווח הדילוג שהוגדר.",
    cls: "bg-sky-100 text-sky-800 border-sky-200",
  },
  [SOURCE_TRUTH.CACHED]: {
    label: "שמור",
    hint: "מוצג מידע מהטעינה המוצלחת האחרונה; אין כרגע רענון פעיל.",
    cls: "bg-amber-50 text-amber-800 border-amber-200",
  },
  [SOURCE_TRUTH.STALE]: {
    label: "לא מעודכן",
    hint: "הרענון האחרון נכשל; מוצג המידע מהטעינה המוצלחת האחרונה בלבד.",
    cls: "bg-orange-100 text-orange-800 border-orange-300",
  },
  [SOURCE_TRUTH.UNAVAILABLE]: {
    label: "לא זמין",
    hint: "לא התקבל מהמקור מידע שאפשר להציג.",
    cls: "bg-gray-100 text-gray-600 border-gray-300",
  },
  [SOURCE_TRUTH.DENIED]: {
    label: "אין הרשאה",
    hint: "השרת דחה את קריאת המקור (401/403).",
    cls: "bg-gray-100 text-gray-600 border-dashed border-gray-400",
  },
  [SOURCE_TRUTH.UNKNOWN]: {
    label: "לא ידוע",
    hint: "אין מידע על מצב המקור.",
    cls: "bg-gray-100 text-gray-500 border-dashed border-gray-300",
  },
  [SOURCE_TRUTH.LOADING]: {
    label: "טוען…",
    hint: "ממתין לתשובת המקור.",
    cls: "bg-gray-50 text-gray-400 border-gray-200",
  },
};

export function sourceTruthMeta(key) {
  const resolved = SOURCE_TRUTH_META[key] ? key : SOURCE_TRUTH.UNKNOWN;
  return { key: resolved, ...SOURCE_TRUTH_META[resolved] };
}

function seconds(ms) {
  return Math.max(1, Math.round(ms / 1000));
}

function makeTruth(key, { since = null, pollingMs = 0, status = null } = {}) {
  const meta = sourceTruthMeta(key);
  let hint = meta.hint;
  if (key === SOURCE_TRUTH.POLLING && pollingMs > 0) {
    hint = `המקור נבדק מחדש כל ${seconds(pollingMs)} שניות; המוצג הוא תשובת השרת האחרונה.`;
  } else if ((key === SOURCE_TRUTH.CACHED || key === SOURCE_TRUTH.STALE) && since) {
    hint = `${meta.hint} הטעינה המוצלחת האחרונה: ${new Date(since).toLocaleTimeString("he-IL")}.`;
  }
  return { key, label: meta.label, cls: meta.cls, hint, since, pollingMs, status };
}

/**
 * Classify a react-query result into the source-truth vocabulary.
 *
 * options:
 *   online      browser online signal (default true — callers should pass the real signal)
 *   enabled     whether the caller's query is enabled (default true)
 *   pollingMs   the refetch interval the caller actually configured (0 = no poller)
 *   polling     explicit override; true only when a poller really runs
 *   hasPayload  whether the page can render a usable payload from this query. It is always gated
 *               on a successful read (dataUpdatedAt > 0); a successful empty response with
 *               hasPayload: false reads as UNAVAILABLE, never POLLING.
 *   liveSource  { connected: true, lastEventAt: <ms> } for a genuine push source only
 *   denied      explicit authorization denial from a caller that knows it
 */
export function classifySourceTruth(query, options = {}) {
  const online = options.online !== false;
  const enabled = options.enabled !== false;
  const pollingMs = Number(options.pollingMs) > 0 ? Number(options.pollingMs) : 0;
  const polling = options.polling === true || pollingMs > 0;
  const liveSource =
    options.liveSource &&
    options.liveSource.connected === true &&
    Number(options.liveSource.lastEventAt) > 0
      ? options.liveSource
      : null;

  if (!query) return makeTruth(SOURCE_TRUTH.UNKNOWN, { pollingMs });

  const hasRead = Number(query.dataUpdatedAt) > 0;
  // A payload claim without a successful read has no evidence behind it.
  const hasPayload = hasRead && (options.hasPayload === undefined || options.hasPayload === true);
  const since = hasRead ? query.dataUpdatedAt : null;
  const status = query?.error?.response?.status ?? null;
  const denied = options.denied === true || status === 401 || status === 403;
  const isError = query.isError === true || query.status === "error";
  const paused = query.fetchStatus === "paused";

  // Authorization denial is the strongest fact: it must never read as held/current data.
  if (denied) return makeTruth(SOURCE_TRUTH.DENIED, { since, pollingMs, status });

  if (hasPayload) {
    // A failed refresh with a held payload is stale, never "polling" and never current.
    if (isError) return makeTruth(SOURCE_TRUTH.STALE, { since, pollingMs, status });
    // Offline, paused or no active query: the held payload is a snapshot, not a live feed.
    if (!online || paused || !enabled) return makeTruth(SOURCE_TRUTH.CACHED, { since, pollingMs });
    // LIVE is a push fact, not a polling claim.
    if (liveSource) return makeTruth(SOURCE_TRUTH.LIVE, { since, pollingMs });
    if (polling) return makeTruth(SOURCE_TRUTH.POLLING, { since, pollingMs });
    return makeTruth(SOURCE_TRUTH.CACHED, { since, pollingMs });
  }

  if (!enabled) return makeTruth(SOURCE_TRUTH.UNKNOWN, { pollingMs });
  if (isError) return makeTruth(SOURCE_TRUTH.UNAVAILABLE, { pollingMs, status });
  if (!online || paused) return makeTruth(SOURCE_TRUTH.UNAVAILABLE, { pollingMs });
  // The read succeeded but yielded nothing usable: that is unavailable, not still loading.
  if (hasRead) return makeTruth(SOURCE_TRUTH.UNAVAILABLE, { pollingMs });
  return makeTruth(SOURCE_TRUTH.LOADING, { pollingMs });
}

/** True when the state is a held snapshot that must not animate as live. */
export function isHeldSnapshotTruth(key) {
  return key === SOURCE_TRUTH.CACHED || key === SOURCE_TRUTH.STALE || key === SOURCE_TRUTH.UNAVAILABLE;
}

/** True only for states backed by a currently-running source. */
export function isCurrentTruth(key) {
  return key === SOURCE_TRUTH.LIVE || key === SOURCE_TRUTH.POLLING;
}
